import React from "react";
import { motion } from "motion/react";
import { MapPin, Search, ArrowRight, Building2, Briefcase, GraduationCap, ChevronRight, Compass, BookOpen, Layers } from "lucide-react";
import { useSeoMetadata } from "../PublicPages";
import { SeoBreadcrumbs } from "./SeoBreadcrumbs";
import { getStateBySlug, getQualifiedLocationsByState } from "../../utils/locationRegistry";

import { generateTier1StateMetadata } from "../../utils/seoProgrammaticEngine";

interface StateHubPageProps {
  stateSlug: string;
  variant?: "jobs" | "businesses-hiring" | "internships";
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
  currentUser?: any;
}

export function StateHubPage({ stateSlug, variant = "jobs", onNavigate, onLogin = () => {}, currentUser }: StateHubPageProps) {
  const stateData = getStateBySlug(stateSlug);

  if (!stateData) {
    return (
      <div className="w-full">
        <main className="max-w-4xl mx-auto px-6 py-20 text-center space-y-4">
          <MapPin className="w-12 h-12 text-slate-400 mx-auto" />
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">Region Hub Not Found</h1>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            We currently do not have a dedicated regional index for "{stateSlug}". Explore our full locations directory.
          </p>
          <a
            href="/locations"
            onClick={(e) => { e.preventDefault(); onNavigate("/locations"); }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-bold rounded-xl text-sm hover:bg-blue-700 transition"
          >
            <ArrowRight className="w-4 h-4" /> Browse Locations Directory
          </a>
        </main>
      </div>
    );
  }

  const locations = getQualifiedLocationsByState(stateData.stateAbbreviation);
  const tier1Meta = generateTier1StateMetadata(stateData.stateName, stateSlug, variant);

  let parentDirectoryUrl = "/jobs/locations";
  let parentDirectoryLabel = "Jobs Locations";

  if (variant === "businesses-hiring") {
    parentDirectoryUrl = "/businesses-hiring/locations";
    parentDirectoryLabel = "Hiring Signals Locations";
  } else if (variant === "internships") {
    parentDirectoryUrl = "/internships/locations";
    parentDirectoryLabel = "Internship Locations";
  }

  useSeoMetadata({
    title: tier1Meta.title,
    description: tier1Meta.metaDescription,
    canonicalUrl: tier1Meta.canonicalUrl
  });

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    onNavigate(path);
  };

  return (
    <div className="w-full">
      <main className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">
        <SeoBreadcrumbs 
          items={[
            { label: "Explore", path: "/explore" },
            { label: "Locations", path: "/locations" },
            { label: parentDirectoryLabel, path: parentDirectoryUrl },
            { label: stateData.stateName, path: `/${variant}/${stateSlug}` }
          ]} 
          onNavigate={onNavigate} 
        />

        {/* Hero Banner */}
        <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 shadow-xs space-y-6">
          <div className="space-y-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-900/50">
              <MapPin className="w-3.5 h-3.5" /> Regional Employer Index
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-950 dark:text-white tracking-tight leading-tight">
              {tier1Meta.h1}
            </h1>
            <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 max-w-3xl leading-relaxed">
              BizNearYou monitors commercial activity, building permits, web expansions, and contactability across {locations.length} major regional markets in {stateData.stateName}. Select a city hub below to discover local opportunities.
            </p>
          </div>
        </section>

        {/* City Cards Grid */}
        <section className="space-y-6">
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              Verified City Markets in {stateData.stateName}
            </h2>
            <span className="text-xs bg-indigo-100 dark:bg-indigo-950/80 text-indigo-800 dark:text-indigo-300 font-bold px-3 py-1 rounded-full border border-indigo-200 dark:border-indigo-900/50">
              {locations.length} City Hubs
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {locations.map((loc) => {
              const targetUrl = variant === "businesses-hiring"
                ? `/businesses-hiring/${loc.citySlug}`
                : variant === "internships"
                ? `/internships/${loc.citySlug}`
                : `/jobs/${loc.citySlug}`;

              return (
                <div
                  key={loc.citySlug}
                  className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-indigo-700 rounded-2xl p-6 shadow-xs hover:shadow-md transition flex flex-col justify-between space-y-4"
                >
                  <div className="space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                          {loc.cityName}, {loc.stateAbbreviation}
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                          {loc.localHighlights.metroName}
                        </p>
                      </div>
                      <span className="text-xs font-mono font-bold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-lg border border-slate-200/60 dark:border-slate-700/60">
                        {loc.businessCount}+ Leads
                      </span>
                    </div>

                    <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-3 leading-relaxed pt-1">
                      {loc.localHighlights.localContextText}
                    </p>
                  </div>

                  <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex flex-col gap-2 text-xs">
                    <a
                      href={targetUrl}
                      onClick={(e) => handleLinkClick(e, targetUrl)}
                      className="w-full flex items-center justify-between px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition cursor-pointer shadow-2xs"
                    >
                      <span className="flex items-center gap-1.5">
                        {variant === "businesses-hiring" && <Building2 className="w-3.5 h-3.5" />}
                        {variant === "internships" && <GraduationCap className="w-3.5 h-3.5" />}
                        {variant === "jobs" && <Briefcase className="w-3.5 h-3.5" />}
                        Explore {loc.cityName} Hub
                      </span>
                        <ArrowRight className="w-3.5 h-3.5" />
                    </a>

                    <div className="flex justify-between items-center text-[11px] text-slate-500 dark:text-slate-400 pt-1">
                      <a href={`/jobs/${loc.citySlug}`} onClick={(e) => handleLinkClick(e, `/jobs/${loc.citySlug}`)} className="hover:text-indigo-600 dark:hover:text-indigo-400">Jobs</a>
                      <span>•</span>
                      <a href={`/businesses-hiring/${loc.citySlug}`} onClick={(e) => handleLinkClick(e, `/businesses-hiring/${loc.citySlug}`)} className="hover:text-indigo-600 dark:hover:text-indigo-400">Hiring Signals</a>
                      <span>•</span>
                      <a href={`/internships/${loc.citySlug}`} onClick={(e) => handleLinkClick(e, `/internships/${loc.citySlug}`)} className="hover:text-indigo-600 dark:hover:text-indigo-400">Internships</a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Cross Directory Links */}
        <section className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-xs">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Compass className="w-5 h-5 text-blue-600 dark:text-blue-400" /> Related Search Directories
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
            <a href="/guides" onClick={(e) => handleLinkClick(e, "/guides")} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl hover:bg-slate-100 dark:hover:bg-slate-750 transition border border-slate-200 dark:border-slate-700 space-y-2 block">
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-bold">
                <BookOpen className="w-4 h-4" /> Career Guides
              </div>
              <p className="text-slate-600 dark:text-slate-300">Learn how to draft cold emails and ask small business owners if they are hiring.</p>
            </a>

            <a href="/industries" onClick={(e) => handleLinkClick(e, "/industries")} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl hover:bg-slate-100 dark:hover:bg-slate-750 transition border border-slate-200 dark:border-slate-700 space-y-2 block">
              <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 font-bold">
                <Layers className="w-4 h-4" /> Industry Directory
              </div>
              <p className="text-slate-600 dark:text-slate-300">Browse healthcare practices, trade contractors, and merchants across industries.</p>
            </a>

            <a href="/locations" onClick={(e) => handleLinkClick(e, "/locations")} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl hover:bg-slate-100 dark:hover:bg-slate-750 transition border border-slate-200 dark:border-slate-700 space-y-2 block">
              <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-bold">
                <MapPin className="w-4 h-4" /> All Locations Directory
              </div>
              <p className="text-slate-600 dark:text-slate-300">View cities and regional markets across all supported regions and countries.</p>
            </a>
          </div>
        </section>

      </main>
    </div>
  );
}
